import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, FileText, Edit, Eye, Lock, Search, Filter, X, RefreshCw, Calendar, Maximize2, Minimize2, Download, FileSpreadsheet, FlaskConical, AlertTriangle, CheckCircle2, Loader2, Pill, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PatientSearchDropdown } from '@/components/SearchDropdowns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import { toast } from 'sonner';
import { downloadCSV, downloadExcel, EXPORT_COLUMNS } from '@/lib/exportUtils';

export default function MedicalRecords() {
  const [records, setRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [labResults, setLabResults] = useState([]);
  const [labResultsLoading, setLabResultsLoading] = useState(false);
  const [showLabResults, setShowLabResults] = useState(false);
  
  // Patient history state for create modal
  const [patientPrescriptions, setPatientPrescriptions] = useState([]);
  const [patientLabOrders, setPatientLabOrders] = useState([]);
  const [patientHistoryLoading, setPatientHistoryLoading] = useState(false);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 10; // Load 10 records at a time for better UX
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [diagnosisFilter, setDiagnosisFilter] = useState('');
  
  const user = JSON.parse(localStorage.getItem('user'));
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: user.id,
    diagnosis: '',
    symptoms: '',
    prescription: '',
    lab_results: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Store patient map for enriching records
  const [patientMap, setPatientMap] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel - paginated records
      const [patientsRes, doctorsRes, recordsRes, countRes] = await Promise.all([
        api.get('/patients'),
        api.get('/users/doctors'),
        api.get(`/medical-records?limit=${PAGE_SIZE}&skip=0`),
        api.get('/medical-records/count')
      ]);
      
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      setTotalCount(countRes.data.count || 0);
      
      // Create patient lookup map for quick access
      const pMap = {};
      patientsRes.data.forEach(p => { pMap[p.id] = p; });
      setPatientMap(pMap);
      
      // Enrich records with patient data
      const enrichedRecords = recordsRes.data.map(record => ({
        ...record,
        patient: pMap[record.patient_id] || null
      }));
      
      setRecords(enrichedRecords);
      setHasMore(enrichedRecords.length >= PAGE_SIZE);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Load more records for pagination
  const loadMoreRecords = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const skip = records.length;
      const response = await api.get(`/medical-records?limit=${PAGE_SIZE}&skip=${skip}`);
      
      if (response.data.length === 0) {
        setHasMore(false);
        return;
      }
      
      // Enrich new records with patient data
      const newRecords = response.data.map(record => ({
        ...record,
        patient: patientMap[record.patient_id] || null
      }));
      
      setRecords(prev => [...prev, ...newRecords]);
      setHasMore(response.data.length >= PAGE_SIZE);
    } catch (error) {
      toast.error('Failed to load more records');
    } finally {
      setLoadingMore(false);
    }
  };

  // Advanced filtering
  const filteredRecords = useMemo(() => {
    let filtered = records;
    
    // Text search
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.patient?.full_name?.toLowerCase().includes(query) ||
        record.patient?.patient_number?.toLowerCase().includes(query) ||
        record.diagnosis?.toLowerCase().includes(query) ||
        record.symptoms?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }
    
    // Doctor filter
    if (doctorFilter && doctorFilter !== 'all') {
      filtered = filtered.filter(record => record.doctor_id === doctorFilter);
    }
    
    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.created_at).toISOString().split('T')[0];
        return recordDate >= dateFrom;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.created_at).toISOString().split('T')[0];
        return recordDate <= dateTo;
      });
    }
    
    // Diagnosis filter
    if (diagnosisFilter.trim()) {
      const diagQuery = diagnosisFilter.toLowerCase();
      filtered = filtered.filter(record => 
        record.diagnosis?.toLowerCase().includes(diagQuery)
      );
    }
    
    return filtered;
  }, [records, searchTerm, statusFilter, doctorFilter, dateFrom, dateTo, diagnosisFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDoctorFilter('all');
    setDateFrom('');
    setDateTo('');
    setDiagnosisFilter('');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || doctorFilter !== 'all' || dateFrom || dateTo || diagnosisFilter;

  // Export handlers
  const handleExportCSV = () => {
    if (filteredRecords.length === 0) {
      toast.error('No data to export');
      return;
    }
    const enrichedData = filteredRecords.map(record => ({
      ...record,
      patient_name: record.patient?.full_name || 'N/A',
      patient_number: record.patient?.patient_number || 'N/A',
      doctor_name: doctors.find(d => d.id === record.doctor_id)?.full_name || 'N/A'
    }));
    downloadCSV(enrichedData, EXPORT_COLUMNS.medicalRecords, 'medical_records_export');
    toast.success(`Exported ${filteredRecords.length} record(s) to CSV`);
  };

  const handleExportExcel = () => {
    if (filteredRecords.length === 0) {
      toast.error('No data to export');
      return;
    }
    const enrichedData = filteredRecords.map(record => ({
      ...record,
      patient_name: record.patient?.full_name || 'N/A',
      patient_number: record.patient?.patient_number || 'N/A',
      doctor_name: doctors.find(d => d.id === record.doctor_id)?.full_name || 'N/A'
    }));
    downloadExcel(enrichedData, EXPORT_COLUMNS.medicalRecords, 'medical_records_export');
    toast.success(`Exported ${filteredRecords.length} record(s) to Excel`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedRecord) {
        await api.put(`/medical-records/${selectedRecord.id}`, formData);
        toast.success('Medical record updated successfully!');
      } else {
        await api.post('/medical-records', formData);
        toast.success('Medical record created successfully!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save medical record');
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      doctor_id: user.id,
      diagnosis: '',
      symptoms: '',
      prescription: '',
      lab_results: '',
      notes: ''
    });
    setEditMode(false);
    setSelectedRecord(null);
    setPatientPrescriptions([]);
    setPatientLabOrders([]);
  };

  // Fetch patient's prescriptions and lab results when patient is selected
  const fetchPatientHistory = async (patientId) => {
    if (!patientId) {
      setPatientPrescriptions([]);
      setPatientLabOrders([]);
      return;
    }
    
    setPatientHistoryLoading(true);
    try {
      const [prescriptionsRes, labOrdersRes] = await Promise.all([
        api.get(`/prescriptions?patient_id=${patientId}`),
        api.get(`/lab-orders/orders?patient_id=${patientId}`)
      ]);
      
      // Get recent prescriptions (last 10 for dropdown)
      const recentPrescriptions = (prescriptionsRes.data || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
      setPatientPrescriptions(recentPrescriptions);
      
      // Get all lab orders (last 10 for dropdown)
      const labOrders = (labOrdersRes.data?.orders || labOrdersRes.data || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
      setPatientLabOrders(labOrders);
      
    } catch (error) {
      console.error('Error fetching patient history:', error);
    } finally {
      setPatientHistoryLoading(false);
    }
  };

  // Handle patient selection in create modal
  const handlePatientSelect = (patientId) => {
    setFormData({...formData, patient_id: patientId});
    fetchPatientHistory(patientId);
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setLabResults([]);
    setShowLabResults(false);
    setViewDialogOpen(true);
    // Auto-fetch lab results when opening the modal
    fetchLabResultsOnOpen(record.id);
  };

  const fetchLabResultsOnOpen = async (recordId) => {
    setLabResultsLoading(true);
    try {
      const response = await api.get(`/medical-records/${recordId}/lab-results`);
      if (response.data.lab_results?.length > 0) {
        setLabResults(response.data.lab_results);
        setShowLabResults(true);
      }
    } catch (error) {
      console.error('Error fetching lab results:', error);
    } finally {
      setLabResultsLoading(false);
    }
  };

  const fetchLabResults = async (recordId) => {
    setLabResultsLoading(true);
    try {
      const response = await api.get(`/medical-records/${recordId}/lab-results`);
      setLabResults(response.data.lab_results || []);
      setShowLabResults(true);
      if (response.data.lab_results?.length === 0) {
        toast.info('No lab results found for this patient/visit');
      }
    } catch (error) {
      toast.error('Failed to fetch lab results');
      console.error('Error fetching lab results:', error);
    } finally {
      setLabResultsLoading(false);
    }
  };

  const attachLabResults = async (recordId) => {
    try {
      const response = await api.post(`/medical-records/${recordId}/attach-lab-results`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh the record to show attached lab results
        const updatedRecord = await api.get(`/medical-records/${recordId}`);
        setSelectedRecord({ ...selectedRecord, lab_results: updatedRecord.data.lab_results });
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to attach lab results');
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setFormData({
      patient_id: record.patient_id,
      doctor_id: record.doctor_id,
      diagnosis: record.diagnosis || '',
      symptoms: record.symptoms || '',
      prescription: record.prescription || '',
      lab_results: record.lab_results || '',
      notes: record.notes || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  // Get unique diagnoses for filter dropdown
  const uniqueDiagnoses = [...new Set(records.map(r => r.diagnosis).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="medical-records-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Medical Records
            </h1>
            <p className="text-slate-600 mt-2">View and manage patient medical records</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                resetForm();
                setIsMaximized(false);
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-medical-record-button" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Record
                </Button>
              </DialogTrigger>
              <DialogContent 
                className={`overflow-y-auto transition-all duration-200 ${
                  isMaximized 
                    ? '!fixed !inset-2 !left-2 !top-2 !right-2 !bottom-2 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-16px)] !h-[calc(100vh-16px)] !max-h-none' 
                    : 'max-w-2xl max-h-[90vh]'
                }`}
              >
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>{editMode ? 'Edit Medical Record' : 'Create Medical Record'}</DialogTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMaximized(!isMaximized)}
                      className="h-8 w-8 p-0 hover:bg-slate-100"
                      title={isMaximized ? "Minimize" : "Maximize"}
                    >
                      {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="patient_id">Patient *</Label>
                    <Select 
                      value={formData.patient_id} 
                      onValueChange={handlePatientSelect} 
                      required
                      disabled={editMode}
                    >
                      <SelectTrigger data-testid="record-patient-select">
                        <SelectValue placeholder="Select patient" />
                      </SelectTrigger>
                      <SelectContent>
                        {patients.map((patient) => (
                          <SelectItem key={patient.id} value={patient.id}>
                            {patient.full_name} ({patient.patient_number})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="diagnosis">Diagnosis *</Label>
                    <Input
                      id="diagnosis"
                      value={formData.diagnosis}
                      onChange={(e) => setFormData({...formData, diagnosis: e.target.value})}
                      data-testid="record-diagnosis-input"
                      placeholder="e.g., Hypertension"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="symptoms">Symptoms</Label>
                    <textarea
                      id="symptoms"
                      value={formData.symptoms}
                      onChange={(e) => setFormData({...formData, symptoms: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
                      placeholder="Describe symptoms..."
                    />
                  </div>

                  {/* Prescription Section with Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="prescription">Prescription</Label>
                    {!editMode && (
                      <Select
                        disabled={!formData.patient_id || patientHistoryLoading || patientPrescriptions.length === 0}
                        onValueChange={(rxId) => {
                          const selectedRx = patientPrescriptions.find(rx => rx.id === rxId);
                          if (selectedRx) {
                            const rxText = selectedRx.medications?.map(med => 
                              `${med.medication_name}${med.dosage ? ` ${med.dosage}` : ''}${med.frequency ? ` - ${med.frequency}` : ''}${med.duration ? ` for ${med.duration}` : ''}`
                            ).join('\n') || '';
                            setFormData({...formData, prescription: rxText});
                          }
                        }}
                      >
                        <SelectTrigger className={`mb-2 border-dashed ${formData.patient_id && patientPrescriptions.length > 0 ? 'border-purple-300 bg-purple-50/50' : 'border-slate-300 bg-slate-50/50'}`}>
                          <div className={`flex items-center gap-2 ${formData.patient_id && patientPrescriptions.length > 0 ? 'text-purple-700' : 'text-slate-400'}`}>
                            <Pill className="h-4 w-4" />
                            <SelectValue placeholder={
                              !formData.patient_id 
                                ? "Select a patient first" 
                                : patientHistoryLoading 
                                  ? "Loading prescriptions..." 
                                  : patientPrescriptions.length === 0 
                                    ? "No prescriptions found for this patient"
                                    : "Select from patient's prescriptions (optional)"
                            } />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {patientPrescriptions.map((rx, index) => (
                            <SelectItem key={rx.id} value={rx.id}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium font-mono text-sm">{rx.prescription_number || rx.id.substring(0, 8)}</span>
                                {index === 0 && (
                                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">Recent</Badge>
                                )}
                                <span className="text-xs text-slate-500">
                                  - {new Date(rx.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <textarea
                      id="prescription"
                      value={formData.prescription}
                      onChange={(e) => setFormData({...formData, prescription: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
                      placeholder="Medications and dosage..."
                    />
                  </div>

                  {/* Lab Results Section with Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="lab_results">Lab Results</Label>
                    {!editMode && (
                      <Select
                        disabled={!formData.patient_id || patientHistoryLoading || patientLabOrders.length === 0}
                        onValueChange={(orderId) => {
                          const selectedOrder = patientLabOrders.find(o => o.id === orderId);
                          if (selectedOrder) {
                            const labText = selectedOrder.tests?.map(test => 
                              `${test.test_name}: ${test.result_value || 'Pending'}${test.unit ? ` ${test.unit}` : ''}${test.reference_range_text ? ` (Ref: ${test.reference_range_text})` : ''}${test.flag && test.flag !== 'normal' ? ` [${test.flag.toUpperCase()}]` : ''}`
                            ).join('\n') || '';
                            setFormData({...formData, lab_results: labText});
                          }
                        }}
                      >
                        <SelectTrigger className={`mb-2 border-dashed ${formData.patient_id && patientLabOrders.length > 0 ? 'border-blue-300 bg-blue-50/50' : 'border-slate-300 bg-slate-50/50'}`}>
                          <div className={`flex items-center gap-2 ${formData.patient_id && patientLabOrders.length > 0 ? 'text-blue-700' : 'text-slate-400'}`}>
                            <TestTube className="h-4 w-4" />
                            <SelectValue placeholder={
                              !formData.patient_id 
                                ? "Select a patient first" 
                                : patientHistoryLoading 
                                  ? "Loading lab results..." 
                                  : patientLabOrders.length === 0 
                                    ? "No lab results found for this patient"
                                    : "Select from patient's lab results (optional)"
                            } />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {patientLabOrders.map((order, index) => (
                            <SelectItem key={order.id} value={order.id}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium font-mono text-sm">{order.order_number || order.id.substring(0, 8)}</span>
                                {index === 0 && (
                                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">Recent</Badge>
                                )}
                                <span className="text-xs text-slate-500">
                                  - {new Date(order.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <textarea
                      id="lab_results"
                      value={formData.lab_results}
                      onChange={(e) => setFormData({...formData, lab_results: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
                      placeholder="Lab test results..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
                      placeholder="Additional observations..."
                    />
                  </div>

                  <Button type="submit" data-testid="submit-medical-record-button" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                    {editMode ? 'Update Record' : 'Create Record'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6 border border-slate-200 bg-white">
          <CardContent className="p-4">
            {/* Search Box */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by Patient ID, Patient Name, Visit ID, Record ID, or Diagnosis..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
                data-testid="medical-records-search"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">Filters:</span>
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="finalized">Finalized</SelectItem>
                </SelectContent>
              </Select>

              {/* Doctor Filter */}
              <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                <SelectTrigger className="w-[180px]" data-testid="doctor-filter">
                  <SelectValue placeholder="Doctor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date From */}
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[150px]"
                  placeholder="From"
                  data-testid="date-from-filter"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center gap-1">
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[150px]"
                  placeholder="To"
                  data-testid="date-to-filter"
                />
              </div>

              {/* Diagnosis Filter */}
              {uniqueDiagnoses.length > 0 && (
                <Select value={diagnosisFilter || "all_diagnoses"} onValueChange={(v) => setDiagnosisFilter(v === "all_diagnoses" ? "" : v)}>
                  <SelectTrigger className="w-[180px]" data-testid="diagnosis-filter">
                    <SelectValue placeholder="Diagnosis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_diagnoses">All Diagnoses</SelectItem>
                    {uniqueDiagnoses.slice(0, 20).map((diagnosis, idx) => (
                      <SelectItem key={idx} value={diagnosis}>
                        {diagnosis.length > 30 ? diagnosis.substring(0, 30) + '...' : diagnosis}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}

              {/* Export Buttons */}
              <div className="ml-auto flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Export as Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-3 text-sm text-slate-500">
              Showing {filteredRecords.length} of {totalCount > 0 ? totalCount : records.length} records
              {hasActiveFilters && ' (filtered)'}
              {hasMore && !hasActiveFilters && records.length < totalCount && (
                <span className="ml-1 text-[#4A238F]">• Scroll down to load more</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Records List */}
        <div className="space-y-4">
          {filteredRecords.length === 0 ? (
            <Card className="border border-slate-200">
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {records.length === 0 
                    ? 'No medical records found' 
                    : 'No records match your search criteria'}
                </p>
                {hasActiveFilters && (
                  <Button 
                    variant="link" 
                    onClick={clearFilters}
                    className="mt-2 text-[#4A238F]"
                  >
                    Clear filters
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredRecords.map((record) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Card 
                  className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleViewRecord(record)}
                  data-testid={`medical-record-card-${record.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-[#0f766e]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{record.patient?.full_name}</h3>
                              <Badge 
                                className={record.status === 'finalized' 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-amber-100 text-amber-700'
                                }
                              >
                                {record.status === 'finalized' && <Lock className="h-3 w-3 mr-1" />}
                                {record.status || 'draft'}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                              <span>{record.patient?.patient_number}</span>
                              {record.visit_id && (
                                <>
                                  <span className="text-slate-300">•</span>
                                  <span>Visit: {record.visit_id.slice(0, 8)}...</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <p className="text-xs text-slate-500">
                              {new Date(record.created_at).toLocaleDateString()}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewRecord(record)}
                              data-testid={`view-record-${record.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {record.status !== 'finalized' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditRecord(record)}
                                data-testid={`edit-record-${record.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <p className="text-sm font-medium text-slate-700">Diagnosis:</p>
                            <p className="text-slate-900">{record.diagnosis}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
          
          {/* Load More Button */}
          {!hasActiveFilters && hasMore && records.length > 0 && (
            <div className="flex justify-center py-6">
              <Button
                variant="outline"
                onClick={loadMoreRecords}
                disabled={loadingMore}
                className="min-w-[200px]"
                data-testid="load-more-records-btn"
              >
                {loadingMore ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Load More Records
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* End of records indicator */}
          {!hasMore && records.length > PAGE_SIZE && (
            <div className="text-center py-4 text-sm text-slate-500">
              All {totalCount} records loaded
            </div>
          )}
        </div>

        {/* View Record Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#0f766e]" />
                Medical Record Details
                {selectedRecord && (
                  <Badge 
                    className={selectedRecord.status === 'finalized' 
                      ? 'bg-emerald-100 text-emerald-700 ml-2' 
                      : 'bg-amber-100 text-amber-700 ml-2'
                    }
                  >
                    {selectedRecord.status === 'finalized' && <Lock className="h-3 w-3 mr-1" />}
                    {selectedRecord.status || 'draft'}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedRecord && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Patient</p>
                      <p className="font-semibold">{selectedRecord.patient?.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Date</p>
                      <p className="font-semibold">{new Date(selectedRecord.created_at).toLocaleDateString()}</p>
                    </div>
                    {selectedRecord.finalized_at && (
                      <div>
                        <p className="text-sm text-slate-600">Finalized At</p>
                        <p className="font-semibold">{new Date(selectedRecord.finalized_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1">Diagnosis</p>
                  <p className="text-slate-900 bg-slate-50 p-3 rounded">{selectedRecord.diagnosis}</p>
                </div>

                {selectedRecord.symptoms && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Symptoms</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded">{selectedRecord.symptoms}</p>
                  </div>
                )}

                {selectedRecord.prescription && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Treatment Plan</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded">{selectedRecord.prescription}</p>
                  </div>
                )}

                {selectedRecord.notes && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Notes</p>
                    <p className="text-slate-900 bg-slate-50 p-3 rounded">{selectedRecord.notes}</p>
                  </div>
                )}

                {/* Lab Results Section */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="h-5 w-5 text-blue-600" />
                      <p className="text-sm font-semibold text-slate-700">Lab Results</p>
                      {labResultsLoading && (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchLabResults(selectedRecord.id)}
                        disabled={labResultsLoading}
                        data-testid="view-lab-results-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => attachLabResults(selectedRecord.id)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid="attach-lab-results-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Attach to Record
                      </Button>
                    </div>
                  </div>

                  {/* Lab Results Table - Auto-loaded */}
                  <div className="bg-slate-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                    {labResultsLoading ? (
                      <div className="text-center py-6 text-slate-500">
                        <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-blue-500" />
                        <p>Loading lab results...</p>
                      </div>
                    ) : labResults.length === 0 ? (
                      <div className="text-center py-6 text-slate-500">
                        <FlaskConical className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p>No lab results found for this patient</p>
                        <p className="text-xs mt-1">Lab results will appear here when available</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-blue-50 border-b border-blue-100">
                              <TableHead className="font-semibold text-blue-900">Test Name</TableHead>
                              <TableHead className="font-semibold text-blue-900">Value</TableHead>
                              <TableHead className="font-semibold text-blue-900">Unit</TableHead>
                              <TableHead className="font-semibold text-blue-900">Reference Range</TableHead>
                              <TableHead className="font-semibold text-blue-900">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {labResults.map((result, index) => (
                              <TableRow 
                                key={index}
                                className={result.is_abnormal ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-100'}
                              >
                                <TableCell className="font-medium text-slate-800">
                                  {result.test_name}
                                </TableCell>
                                <TableCell className={result.is_abnormal ? 'text-red-600 font-bold' : 'text-slate-900 font-medium'}>
                                  {result.value || '-'}
                                </TableCell>
                                <TableCell className="text-slate-600">
                                  {result.unit || '-'}
                                </TableCell>
                                <TableCell className="text-slate-600">
                                  {result.reference_range || '-'}
                                </TableCell>
                                <TableCell>
                                  {result.is_abnormal ? (
                                    <Badge className="bg-red-100 text-red-700 border border-red-200">
                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                      Abnormal
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      Normal
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <p className="text-xs text-slate-500 mt-3">
                          Showing {labResults.length} test result(s) from lab orders
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleEditRecord(selectedRecord);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Record
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}